import React from 'react'

import { Route, Routes } from 'react-router-dom'

import axios from 'axios'
import PropTypes from 'prop-types'

import PageLayout from 'Pods/Layout/PageLayout'
import ContextBasedRouter from 'Pods/Shared/ContextBasedRouter'
import i18n from 'Pods/Shared/I18n'

import CustomBrowseHead from './ProjectApplication/Components/CustomBrowseHead'
import ProjectApplication from './ProjectApplication/ProjectApplication'
import ProjectsEditForm from './ProjectsForm/ProjectsEditForm'
import ProjectsForm from './ProjectsForm/ProjectsForm'
import ProjectsFormModal from './ProjectsForm/ProjectsFormModal'
import ProjectsRepostForm from './ProjectsForm/ProjectsRepostForm'
import ProjectsReviewForm from './ProjectsForm/ProjectsReviewForm'
import ProjectsList from './ProjectsList/ProjectsList'

class Projects extends React.Component {
  constructor() {
    super()

    this.state = {
      filters: Projects.getPreferredFilters(),
      projectsCount: 0,
      modalIsOpened: false,
    }

    this.openCreateProject = this.openCreateProject.bind(this)
    this.closeCreateProject = this.closeCreateProject.bind(this)

    this.cancelSource = axios.CancelToken.source()
    this.fetchData()
  }

  localizedPath = (pathname) => {
    const { locale } = this.props.navigation
    return locale === 'en' ? pathname : `/${locale}${pathname}`
  }

  componentDidCatch(error, errorInfo) {
    window.Sentry.captureException(error, { extra: errorInfo })
    window.alert(`You are not supposed to see this, please contact our support.\n\nError: ${error.message}\n\nStack: ${error.stack}\n\nComponent: ${errorInfo.componentStack}`)
  }

  openCreateProject() {
    this.props.layout.isSignedIn ? this.setState({ modalIsOpened: true }) : App.events.publish('app:login:open')
  }

  closeCreateProject() {
    this.setState({ modalIsOpened: false })
  }

  onChange = (newFilters) => {
    this.setState({ filters: newFilters })

    this.fetchData(newFilters)
  }

  static getDefaultFilters() {
    return {
      software: [],
      skills: [],
    }
  }

  static getPreferredFilters() {
    let filters = {}

    try {
      filters = JSON.parse(sessionStorage.getItem('projects/filters')) || {}
    } catch (_e) {

    }

    return { ...Projects.getDefaultFilters(), ...filters }
  }

  checkIfFiltersAreUsed = () => {
    const { filters } = this.state
    return filters.skills.length > 0 || filters.software.length > 0
  }

  getFilterLabel(filters) {
    if (filters.skills.length === 1 && filters.software.length === 0) {
      const filter = this.props.layout.skillsFilterOptions.find((option) => option.value === filters.skills[0])
      return filter.label
    }
    if (filters.skills.length === 0 && filters.software.length === 1) {
      const filter = this.props.layout.softwareFilterOptions.find((option) => option.value === filters.software[0])
      return filter.label
    }
    return 'Filtered'
  }

  fetchData(filters = { ...this.state.filters }) {
    axios.get('/api/internal/projects', {params: filters, cancelToken: this.cancelSource.token })
      .then(this.onFetchDataSuccess)
      .catch(() => {})
  }

  onFetchDataSuccess = (response) => {
    this.setState({ projectsCount: response.data.meta.totalCount })
  }

  render() {
    const breadcrumbsBase = [
      {
        title: 'CGTrader',
        url: '/',
        isActive: false,
      },
    ]

    const customHeader = (
      <CustomBrowseHead
        navContainerClass={this.props.navContainerClass}
        navClass={this.props.navClass}
        navId={this.props.navId}
        navigation={this.props.navigation}
        isSignedIn={this.props.layout.isSignedIn}
        openCreateProject={this.openCreateProject}
      />
    )
    const modelingJob = i18n.t('projects.modelingJob', { ns: 'rails', lng: this.props.navigation.locale } )
    const myProjects = i18n.t('projects.myProjects', { ns: 'rails', lng: this.props.navigation.locale } )
    const projects = i18n.t('projects.projects', { ns: 'rails', lng: this.props.navigation.locale } )
    const reviewProjectApplication = i18n.t('projects.reviewProjectApplication', { ns: 'rails', locale: this.props.navigation.locale } )
    const startAProject = i18n.t('projects.startAProject', { ns: 'rails', lng: this.props.navigation.locale } )
    const editProject = i18n.t('projects.editProject', { ns: 'rails', lng: this.props.navigation.locale } )
    const reviewProject = i18n.t('projects.reviewProject', { ns: 'rails', lng: this.props.navigation.locale } )
    const applyToProject = i18n.t('projects.applyToProject', { ns: 'rails', lng: this.props.navigation.locale } )
    const editProjectApplication = i18n.t('projects.editProjectApplication', { ns: 'rails', lng: this.props.navigation.locale } )

    const paths = [
      {
        path: '/3d-modeling-jobs/new/:activeTab?/:username?',
        component: ProjectsForm,
        breadcrumbs: this.props.layout.newRoyaltyUi ? [] : [
          {
            title: modelingJob,
            url: this.localizedPath('/3d-modeling-jobs'),
            isActive: false,
          },
          { title: startAProject, isActive: true },
        ],
      },
      {
        path: '/:locale?/3d-modeling-jobs/browse/:filter?',
        component: ProjectsList,
        breadcrumbs: [],
        customHeader,
        locale: this.props.navigation.locale,
        skillsFilterOptions: this.props.layout.skillsFilterOptions,
        softwareFilterOptions: this.props.layout.softwareFilterOptions,
      },
      {
        path: '/3d-modeling-jobs/:id/edit',
        component: ProjectsEditForm,
        breadcrumbs: [
          {
            title: myProjects,
            url: '/profile/projects',
            isActive: false,
          },
          { title: editProject, isActive: true },
        ],
      },
      {
        path: '/3d-modeling-jobs/:id/review',
        component: ProjectsReviewForm,
        breadcrumbs: [
          {
            title: myProjects,
            url: '/profile/projects',
            isActive: false,
          },
          { title: reviewProject, isActive: true },
        ],
      },
      {
        path: '/3d-modeling-jobs/:id/repost',
        component: ProjectsRepostForm,
        breadcrumbs: [
          {
            title: myProjects,
            url: '/profile/projects',
            isActive: false,
          },
          { title: 'Repost Project', isActive: true },
        ],
      },
      {
        path: '/:locale?/3d-modeling-jobs/browse/:id/apply',
        component: ProjectApplication,
        breadcrumbs: [
          {
            title: '3D Modeling jobs',
            url: this.localizedPath('/3d-modeling-jobs'),
            isActive: false,
          },
          { title: 'Browse', url: this.localizedPath('/3d-modeling-jobs/browse'), isActive: false },
          { title: applyToProject, isActive: true },
        ],
        isSignedIn: this.props.layout.isSignedIn,
        skillsFilterOptions: this.props.layout.skillsFilterOptions,
        softwareFilterOptions: this.props.layout.softwareFilterOptions,
      },
      {
        path: '/3d-modeling-jobs/browse/:id/:application_id/edit',
        component: ProjectApplication,
        breadcrumbs: [
          {
            title: projects,
            url: this.localizedPath('/3d-modeling-jobs/browse'),
            isActive: false,
          },
          { title: editProjectApplication, isActive: true },
        ],
        isSignedIn: this.props.layout.isSignedIn,
        skillsFilterOptions: this.props.layout.skillsFilterOptions,
        softwareFilterOptions: this.props.layout.softwareFilterOptions,
      },
      {
        path: '/:locale?/3d-modeling-jobs/browse/:id/:application_id/review',
        component: ProjectApplication,
        breadcrumbs: [
          {
            title: projects,
            url: this.localizedPath('/3d-modeling-jobs/browse'),
            isActive: false,
          },
          { title: reviewProjectApplication, isActive: true },
        ],
        isSignedIn: this.props.layout.isSignedIn,
        skillsFilterOptions: this.props.layout.skillsFilterOptions,
        softwareFilterOptions: this.props.layout.softwareFilterOptions,
      },
    ]

    const footerModifier = {
      ...(this.props.footer || {}),
      isReduced: this.props.layout.reducedFooter,
    }

    return (
      <ContextBasedRouter
        initialPathname={this.props.navigation.currentPathname}
      >
        {this.state.modalIsOpened && (
          <ProjectsFormModal
            onModalClose={this.closeCreateProject}
            metaSource="projects_listing"
          />
        )}

        <Routes>
          {paths.map((path, index) => (
            <Route
              key={index}
              path={path.path}
              element={(
                <PageLayout
                  component={path.component}
                  navigation={this.props.navigation}
                  footer={footerModifier}
                  navClass={this.props.navClass}
                  navContainerClass={this.props.navContainerClass}
                  navId={this.props.navId}
                  isSignedIn={path.isSignedIn}
                  breadcrumbs={
                    path.breadcrumbs.length > 0
                      ? breadcrumbsBase.concat(path.breadcrumbs)
                      : []
                  }
                  customHeader={path.customHeader}
                  metaDescription={this.props.layout.metaDescription}
                  button={path.button}
                  locale={path.locale}
                  skillsFilterOptions={path.skillsFilterOptions}
                  softwareFilterOptions={path.softwareFilterOptions}
                  onChange={this.onChange}
                />
              )}
            />
          ))}
        </Routes>
      </ContextBasedRouter>
    )
  }
}

Projects.propTypes = {
  navigation: PropTypes.object.isRequired,
  navClass: PropTypes.string.isRequired,
  navId: PropTypes.string.isRequired,
  layout: PropTypes.object.isRequired,
  footer: PropTypes.object.isRequired,
}

export default Projects
